/**
 * CPT duration rules shared by every appointment save path (Jun 2026).
 * Duplicate CPT codes (same code, different description/display name) are
 * fully supported: resolution prefers the appointment's exact cpt_code_id,
 * then the full service_type label ("name - description"), and returns
 * null instead of guessing when multiple rows remain ambiguous.
 */
export function resolveAppointmentCpt({ cpt_code_id, service_type }, cptCodes) {
  const list = cptCodes || [];
  if (cpt_code_id) {
    const byId = list.find((c) => c.id === cpt_code_id);
    if (byId) return byId;
  }
  if (!service_type) return null;
  const exact = list.find(
    (c) => `${c.display_name || c.code} - ${c.description || ''}` === service_type,
  );
  if (exact) return exact;
  const byDesc = list.filter((c) => c.description && service_type.includes(c.description));
  if (byDesc.length === 1) return byDesc[0];
  const byText = list.filter(
    (c) =>
      (c.display_name && service_type.includes(c.display_name)) ||
      (c.code && service_type.includes(c.code)),
  );
  if (byText.length === 1) return byText[0];
  return null;
}

export function validateCptDuration(form, cptCodes) {
  if (!form) return null;
  const startMs = new Date(form.start_time).getTime();
  const endMs = new Date(form.end_time).getTime();
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs <= startMs) {
    return 'End time must be after the start time — change not saved.';
  }
  if (form.appointment_type !== 'client_session') return null;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  const durationMin = Math.round((endMs - startMs) / 60000);
  const cpt = resolveAppointmentCpt(form, cptCodes);
  if (!cpt) return null;
  const label = cpt.code || cpt.display_name || 'this CPT code';
  if (cpt.max_duration && durationMin > cpt.max_duration) {
    return `Maximum duration for ${label} is ${cpt.max_duration} minutes — change not saved. Please shorten the appointment.`;
  }
  if (cpt.min_duration && durationMin < cpt.min_duration) {
    return `Minimum duration for ${label} is ${cpt.min_duration} minutes — change not saved. Please lengthen the appointment.`;
  }
  if (cpt.allows_multiple_units === false) {
    // max_duration (when set) wins over the single-unit heuristic.
    const singleUnit = cpt.max_duration || cpt.minutes_per_unit || cpt.duration_minutes || 60;
    if (durationMin > singleUnit) {
      return `${label} does not allow multiple units — maximum is ${singleUnit} minutes. Change not saved.`;
    }
  }
  return null;
}
